import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { Alert, Modal, Platform, Pressable, Text, View } from 'react-native';

export type ConfirmationActionKey = string;

export interface ConfirmationAction {
  key: ConfirmationActionKey;
  label: string;
  description?: string;
  variant?: 'primary' | 'secondary' | 'destructive' | 'ghost';
  role?: 'default' | 'cancel' | 'destructive';
}

export interface ConfirmationOptions {
  title: string;
  message?: string;
  actions: ConfirmationAction[];
  dismissOnBackdrop?: boolean;
}

interface ConfirmationContextValue {
  confirm: (options: ConfirmationOptions) => Promise<ConfirmationActionKey | null>;
  confirmDestructive: (options: {
    title: string;
    message?: string;
    confirmLabel?: string;
    cancelLabel?: string;
  }) => Promise<boolean>;
}

const ConfirmationContext = createContext<ConfirmationContextValue | null>(null);

function resolveActionVariant(action: ConfirmationAction): ConfirmationAction['variant'] {
  if (action.variant) return action.variant;
  if (action.role === 'destructive') return 'destructive';
  if (action.role === 'cancel') return 'secondary';
  return 'primary';
}

function getActionContainerClassName(variant: NonNullable<ConfirmationAction['variant']>) {
  return {
    primary: 'bg-forest-600 dark:bg-forest-600',
    secondary: 'bg-gray-100 dark:bg-gray-700',
    destructive: 'bg-red-600 dark:bg-red-700',
    ghost: 'bg-transparent',
  }[variant];
}

function getActionLabelClassName(variant: NonNullable<ConfirmationAction['variant']>) {
  return {
    primary: 'text-white',
    secondary: 'text-gray-800 dark:text-gray-200',
    destructive: 'text-white',
    ghost: 'text-forest-600 dark:text-forest-500',
  }[variant];
}

function getActionDescriptionClassName(variant: NonNullable<ConfirmationAction['variant']>) {
  return {
    primary: 'text-forest-50',
    secondary: 'text-gray-500 dark:text-gray-400',
    destructive: 'text-red-50',
    ghost: 'text-gray-500 dark:text-gray-400',
  }[variant];
}

export function ConfirmationProvider({ children }: { children: React.ReactNode }) {
  const resolverRef = useRef<((value: ConfirmationActionKey | null) => void) | null>(null);
  const [pending, setPending] = useState<ConfirmationOptions | null>(null);

  const close = useCallback((value: ConfirmationActionKey | null) => {
    const resolver = resolverRef.current;
    resolverRef.current = null;
    setPending(null);
    resolver?.(value);
  }, []);

  const confirm = useCallback((options: ConfirmationOptions) => {
    const cancelActionKey =
      options.actions.find((action) => action.role === 'cancel')?.key ?? null;

    if (Platform.OS !== 'web') {
      return new Promise<ConfirmationActionKey | null>((resolve) => {
        Alert.alert(
          options.title,
          options.message,
          options.actions.map((action) => ({
            text: action.label,
            style:
              action.role === 'cancel'
                ? 'cancel'
                : action.role === 'destructive' || action.variant === 'destructive'
                  ? 'destructive'
                  : 'default',
            onPress: () => resolve(action.key),
          })),
          {
            cancelable: options.dismissOnBackdrop !== false,
            onDismiss: () => resolve(cancelActionKey),
          },
        );
      });
    }

    if (resolverRef.current) {
      resolverRef.current(null);
      resolverRef.current = null;
    }

    setPending(options);
    return new Promise<ConfirmationActionKey | null>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const confirmDestructive = useCallback<ConfirmationContextValue['confirmDestructive']>(
    async ({ title, message, confirmLabel = 'Remove', cancelLabel = 'Cancel' }) => {
      const result = await confirm({
        title,
        message,
        actions: [
          { key: 'cancel', label: cancelLabel, role: 'cancel' },
          { key: 'confirm', label: confirmLabel, role: 'destructive' },
        ],
      });

      return result === 'confirm';
    },
    [confirm],
  );

  const value = useMemo<ConfirmationContextValue>(
    () => ({ confirm, confirmDestructive }),
    [confirm, confirmDestructive],
  );

  const cancelActionKey =
    pending?.actions.find((action) => action.role === 'cancel')?.key ?? null;

  return (
    <ConfirmationContext.Provider value={value}>
      {children}
      <Modal
        visible={pending !== null}
        transparent
        animationType="fade"
        onRequestClose={() => close(cancelActionKey)}
      >
        <Pressable
          className="flex-1 items-center justify-center bg-black/50 p-6"
          onPress={() => {
            if (pending?.dismissOnBackdrop === false) return;
            close(cancelActionKey);
          }}
        >
          <Pressable
            className="w-full max-w-sm rounded-2xl bg-white p-5 dark:bg-gray-800"
            onPress={(e) => e.stopPropagation()}
          >
            <Text className="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
              {pending?.title}
            </Text>
            {!!pending?.message && (
              <Text className="mb-4 text-sm text-gray-500 dark:text-gray-400">
                {pending.message}
              </Text>
            )}
            <View className="gap-2">
              {pending?.actions.map((action) => {
                const variant = resolveActionVariant(action) ?? 'primary';

                return (
                  <Pressable
                    key={action.key}
                    className={`rounded-xl px-3 py-3 ${getActionContainerClassName(variant)}`}
                    onPress={() => close(action.key)}
                  >
                    <Text className={`text-sm font-semibold ${getActionLabelClassName(variant)}`}>
                      {action.label}
                    </Text>
                    {!!action.description && (
                      <Text className={`mt-1 text-xs ${getActionDescriptionClassName(variant)}`}>
                        {action.description}
                      </Text>
                    )}
                  </Pressable>
                );
              })}
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </ConfirmationContext.Provider>
  );
}

export function useConfirmationDialog() {
  const value = useContext(ConfirmationContext);
  if (!value) {
    throw new Error('useConfirmationDialog must be used within a ConfirmationProvider');
  }
  return value;
}
